import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import PayPalPayment from '../../components/PayPalPayment';
import { 
  Ship, 
  ArrowLeft,
  Loader2,
  CheckCircle,
  Package,
  MapPin,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatCurrency } from '../../lib/utils';
import { cargoLabel } from '../../components/booking/BulkFields';

function BookingPaymentPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(function() {
    loadBooking();
  }, [bookingId]);

  async function loadBooking() {
    setLoading(true);
    try {
      const response = await api.get('/bookings/' + bookingId);
      setBooking(response.data);
    } catch (err) {
      console.error('Failed to load booking:', err);
      setError(err.response?.data?.detail || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  }

  function handlePaymentSuccess(result) {
    // Reload booking to get updated status
    loadBooking();
  }

  function handlePaymentError(err) {
    console.error('Payment error:', err);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-sm shadow-card p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-medium tracking-[-0.01em] text-ink mb-2">Booking not found</h2>
          <p className="text-gray-600 mb-6">{error || 'The requested booking could not be found.'}</p>
          <Link to="/dashboard/bookings">
            <Button className="bg-ink hover:bg-ink-800 rounded-sm">
              Back to Bookings
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isPaid = booking.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-ink py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/dashboard/bookings" className="inline-flex items-center text-white/70 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Bookings
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-sm flex items-center justify-center">
              <Ship className="h-7 w-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-normal tracking-[-0.01em] text-white">
                {isPaid ? 'Payment Complete' : 'Complete Payment'}
              </h1>
              <p className="text-white/70">Booking: {booking.booking_number}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Booking Details */}
            <div className="bg-white rounded-sm shadow-card p-6">
              <h2 className="text-xl font-medium tracking-[-0.01em] text-ink mb-6 flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Booking Details
              </h2>

              <div className="space-y-4">
                {/* Route */}
                <div className="bg-gray-50 rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">From</p>
                      <p className="font-medium text-ink">{booking.origin_port}</p>
                    </div>
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <Ship className="h-4 w-4 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 mb-1">To</p>
                      <p className="font-medium text-ink">{booking.destination_port}</p>
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-sm p-3">
                    <p className="text-xs text-gray-500">Container</p>
                    <p className="font-medium text-ink">{cargoLabel(booking)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-sm p-3">
                    <p className="text-xs text-gray-500">Cargo type</p>
                    <p className="font-medium text-ink">{booking.cargo_type}</p>
                  </div>
                  <div className="bg-gray-50 rounded-sm p-3">
                    <p className="text-xs text-gray-500">Weight</p>
                    <p className="font-medium text-ink">{booking.weight?.toLocaleString()} kg</p>
                  </div>
                  <div className="bg-gray-50 rounded-sm p-3">
                    <p className="text-xs text-gray-500">Status</p>
                    <Badge className={
                      booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      booking.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-700'
                    }>
                      {booking.status}
                    </Badge>
                  </div>
                </div>

                {/* Vessel Info */}
                {booking.vessel_name && (
                  <div className="bg-gray-50 rounded-sm p-4">
                    <p className="text-xs text-gray-500 mb-1">Assigned vessel</p>
                    <p className="font-medium text-ink">{booking.vessel_name}</p>
                    {booking.voyage_number && (
                      <p className="text-sm text-gray-600">Voyage: {booking.voyage_number}</p>
                    )}
                  </div>
                )}

                {/* Dates */}
                <div className="flex items-center gap-4 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <div>
                    <span className="text-gray-500">Created: </span>
                    <span className="font-medium">{format(parseISO(booking.created_at), 'dd MMM yyyy')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Section */}
            <div className="bg-white rounded-sm shadow-card p-6">
              <PayPalPayment
                booking={booking}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
              />
            </div>
          </div>

          {/* Back Button */}
          <div className="mt-6 text-center">
            <Link to="/dashboard/bookings">
              <Button variant="outline" className="rounded-sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to My Bookings
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BookingPaymentPage;
